import React, { useId } from 'react'
import { TextField, Select } from '../../ui'
import { CreateTaskAssigneeSelector } from './CreateTaskAssigneeSelector.jsx'

const PRIORITY_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
]

const STATUS_OPTIONS = [
  { value: 'TODO', label: 'Todo' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELLED', label: 'Cancelled' },
]

/**
 * Presentational form body for the create-task modal. State is hoisted
 * up to <CreateTaskModal> via `draft` + `onChange`. The modal owns the
 * title-input ref so it can hand it to <Modal initialFocusRef> for
 * post-mount focus.
 *
 * Issue #243 trimmed this form down: the description textarea was
 * removed (it was never rendered anywhere) and the pair selector was
 * folded into a single smart assignee selector.
 */
export function CreateTaskForm({
  draft,
  onChange,
  pairs,
  currentUserId,
  errorMessage,
  titleError,
  titleRef,
}) {
  const dueDateId = useId()

  function patch(field) {
    return (value) => onChange({ [field]: value })
  }

  function clearDueDate() {
    onChange({ dueDate: null })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {errorMessage && (
        <div role="alert" style={styles.errorBanner}>
          {errorMessage}
        </div>
      )}

      <TextField
        ref={titleRef}
        label="Task title"
        required
        value={draft.title}
        onChange={(e) => patch('title')(e.target.value)}
        maxLength={200}
        error={titleError || undefined}
      />

      <div className="field">
        <label className="label" data-required="true">Assignee</label>
        <CreateTaskAssigneeSelector
          value={draft.assignment}
          onChange={patch('assignment')}
          pairs={pairs}
          currentUserId={currentUserId}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <div style={{ flex: 1 }}>
          <Select
            label="Priority"
            value={draft.priority}
            onChange={(e) => patch('priority')(e.target.value)}
            options={PRIORITY_OPTIONS}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="label" htmlFor={dueDateId}>Due date</label>
          <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
            <input
              id={dueDateId}
              type="date"
              className="input"
              value={draft.dueDate ?? ''}
              onChange={(e) => patch('dueDate')(e.target.value || null)}
              aria-label="Due date"
              style={{ flex: 1 }}
            />
            {draft.dueDate && (
              <button
                type="button"
                onClick={clearDueDate}
                aria-label="Clear due date"
                style={styles.clearBtn}
              >×</button>
            )}
          </div>
        </div>
      </div>

      <Select
        label="Status"
        value={draft.status}
        onChange={(e) => patch('status')(e.target.value)}
        options={STATUS_OPTIONS}
      />
    </div>
  )
}

const styles = {
  clearBtn: {
    fontSize: 14,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    padding: '0 4px',
  },
  errorBanner: {
    fontSize: 12,
    padding: '8px 12px',
    background: 'var(--danger-bg)',
    color: 'var(--danger-text)',
    borderRadius: 'var(--radius-md)',
  },
}
